import React, { ReactElement, ReactNode } from 'react'
import { StyledEngineProvider } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'

interface Props {
  children: ReactNode
}

export const Colors = {
  white: '#FFFFFF',
  black: '#000000',
  error: '#DC7C7C',
  dark: '#130827',
  darkGrey: '#130827',
  yellow: '#FFFF00',
  lightYellow: '#F8BB4D',
}

export const ThemeConfig = ({ children }: Props): ReactElement => {
  const theme = createTheme({
    typography: {
      fontFamily: ['Montserrat', 'sans-serif'].join(','),
      h1: {
        fontSize: 40,
        fontWeight: 700,
      },
      h2: {
        fontSize: 24,
        fontWeight: 700,
      },
      h3: {
        fontSize: 18,
        fontWeight: 700,
      },
      subtitle1: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.25,
        opacity: '0.6',
        color: Colors.white,
      },
    },

    palette: {
      primary: {
        main: '#000000', // theme.palette.primary.main
        light: '#FFFFFF', // theme.palette.primary.light
        dark: '#3E3E3E', // theme.palette.primary.dark
      },
      secondary: {
        main: '#29DB8E', // theme.palette.secondary.main
        light: '#51E5A5', // theme.palette.secondary.light
        dark: '#1B184A', // theme.palette.secondary.dark
      },
      info: {
        main: '#4338C9', // theme.palette.info.main
        light: '#584DE2', // theme.palette.info.light
      },
      error: {
        main: '#FF4025', // theme.palette.error.main
      },
    },

    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1140,
        xl: 1920,
      },
    },

    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: '#3E3E3E',
            fontSize: 16,
          },
          asterisk: {
            color: '#51E5A5',
            '&$error': {
              color: '#51E5A5',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              borderColor: 'rgba(62,62,62,0.6)',
            },
            '& input': {
              color: 'rgba(62,62,62,1)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(62,62,62,1)',
              borderWidth: 1,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#51E5A5',
              borderWidth: 1,
            },
          },
          input: {
            color: `${Colors.white}`,
            opacity: '0.87',
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px #43454a inset',
              WebkitTextFillColor: `${Colors.white}`,
            },
          },
        },
      },
    },
  })

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </StyledEngineProvider>
  )
}

export const ThemeDashboardConfig = ({ children }: Props): ReactElement => {
  const P = {
    backgroundDefault: '#0F0622',
    backgroundPaper: 'rgba(216,216,216, 0.05)',
    primaryMain: '#000000',
    primaryLight: '#FFFFFF',
    primaryDark: '#130827',
    secondaryMain: '#685BC6',
    secondaryMain1: '#A442F9',
    secondaryLight: '#AC88FF',
    secondaryDark: '#17072B',
    infoMain: '#150829',
    infoLight: '#17072B',
    infoDark: '#43454A',
    errorMain: '#D64968',
    textOnDark: '#FFFFFF',
    outlineLight: 'rgba(255,255,255,0.6)',
    outlineDark: 'rgba(62,62,62,0.6)',
  }

  const themeDashboard = createTheme({
    typography: {
      h1: { fontSize: 32, fontWeight: 400, lineHeight: 1.25, color: '#FFFFFF' },
      h2: { fontSize: 28, fontWeight: 500, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
      h3: { fontSize: 26, fontWeight: 500, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
      h4: { fontSize: 22, fontWeight: 500, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
      h5: { fontSize: 20, fontWeight: 500, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
      h6: { fontSize: 18, fontWeight: 400, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
      subtitle1: { fontSize: 16, fontWeight: 500, lineHeight: 1.25, opacity: '0.87' as any, color: '#FFFFFF' }, // eslint-disable-line @typescript-eslint/no-explicit-any
    },
    palette: {
      background: {
        default: P.backgroundDefault,
        paper: P.backgroundPaper,
      },
      primary: {
        main: P.primaryMain,
        light: P.primaryLight,
        dark: P.primaryDark,
      },
      secondary: {
        main: P.secondaryMain,
        light: P.secondaryLight,
      },
      error: {
        main: P.errorMain,
      },
      info: {
        main: P.infoMain,
        light: P.infoLight,
        dark: P.infoDark,
      },
    },
    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1140, xl: 1920 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            background: '#17072B',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            background: P.infoMain ?? '#43454A',
            '.MuiDialogContent-root': {
              '& .PrivatePickersToolbar-root': { display: 'none' },
              '& .MuiCalendarPicker-root': { backgroundColor: P.infoMain },
              '& .MuiPickersDay-root': {
                color: P.primaryLight,
                backgroundColor: 'transparent',
                '&.Mui-selected': { backgroundColor: P.secondaryMain },
              },
              '& .MuiSvgIcon-root': { fill: P.primaryLight },
              '& .PrivatePickersSlideTransition-root': {
                minHeight: 235,
                '& .MuiPickersDay-today': {
                  border: 'none',
                  color: P.secondaryMain,
                },
              },
              '& .MuiButtonBase-root': {
                '&.Mui-disabled': { opacity: 0.5 },
              },
              '& .MuiTypography-root': { color: P.primaryLight },
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: P.secondaryMain,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': { borderColor: P.outlineLight },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.white,
              borderWidth: 1,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: P.secondaryMain,
              borderWidth: 1,
            },
          },
          input: {
            color: Colors.white,
            opacity: '0.87' as any, // eslint-disable-line @typescript-eslint/no-explicit-any
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${P.infoDark ?? '#43454A'} inset`,
              WebkitTextFillColor: Colors.white,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: Colors.error,
            '&$error': { color: Colors.error } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: { color: P.primaryDark, fontSize: 16 },
          asterisk: {
            color: P.secondaryMain,
            '&$error': { color: P.secondaryMain } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            color: Colors.white,
            background: '#685BC6',
            textTransform: 'none',
            boxShadow: 'none',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 1.25,
            borderRadius: 8,
            padding: '6px 16px',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:active': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            background: P.backgroundPaper,
          },
        },
      },
    },
  })

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themeDashboard}>{children}</ThemeProvider>
    </StyledEngineProvider>
  )
}
